import { GameEngine, GameEvent, Seat, clone, err } from "../engine.js";
import { RngState, shuffle } from "../rng.js";

/**
 * Jackaroo (جاكارو) — Samra rules v1. Full rule text: docs/games/jackaroo.md
 *
 * Board: 76-square loop, 4 home slots per player. 4 players play as two
 * teams (seats 0+2 vs 1+3); 2 players play head to head.
 * Cards: A = exit or 1 or 11 · K = exit or 13 · Q = 12 · J = swap ·
 * 10 = 10 · 7 = split between two marbles · 5 = move ANY marble 5 ·
 * 4 = back 4 · 2,3,6,8,9 = forward.
 * A marble on its own start square is protected and blocks passing.
 * Landing on another marble sends it back to base. You must play if you
 * can; otherwise you discard a card. When all your marbles are home you
 * play your partner's marbles.
 */

export const JK_TRACK = 76;
export const JK_HOME = 4;
const SIDE = 19;

export interface JackarooOptions {
  /** Cards dealt per round, cycling. */
  dealPattern: number[];
}

export interface MarbleRef {
  s: Seat;
  i: number;
}

export interface JackarooState {
  n: number;
  boardPos: number[]; // board side per seat
  marbles: number[][]; // -1 base, 0..75 track progress, 76..79 home slots
  hands: number[][]; // card ids 0..51
  deck: number[];
  discard: number[];
  turn: Seat;
  dealer: Seat;
  dealIndex: number;
  over: boolean;
  winners: Seat[];
  options: JackarooOptions;
  rng: RngState;
  lastPlay: { seat: Seat; card: number; action: string } | null;
}

export type JackarooAction =
  | { action: "exit" }
  | { action: "move"; marble: MarbleRef; steps: number }
  | { action: "back"; marble: MarbleRef }
  | { action: "swap"; marble: MarbleRef; target: MarbleRef }
  | { action: "split"; parts: { marble: MarbleRef; steps: number }[] };

export type JackarooMove =
  | ({ type: "play"; card: number } & JackarooAction)
  | { type: "discard"; card: number };

export interface JackarooView {
  n: number;
  boardPos: number[];
  marbles: number[][];
  hand: number[];
  handCounts: number[];
  deckCount: number;
  topDiscard: number | null;
  turn: Seat;
  over: boolean;
  winners: Seat[];
  teams: boolean;
  lastPlay: JackarooState["lastPlay"];
  legal: JackarooMove[];
  mustDiscard: boolean;
}

export const cardRank = (c: number) => (c % 13) + 1; // 1=A … 11=J 12=Q 13=K
export const cardSuit = (c: number) => Math.floor(c / 13);

const teams = (s: JackarooState) => s.n === 4;
const partner = (s: JackarooState, seat: Seat) => (teams(s) ? (seat + 2) % 4 : -1);
const startAbs = (s: JackarooState, seat: Seat) => s.boardPos[seat] * SIDE;
const absOf = (s: JackarooState, seat: Seat, rel: number) => (startAbs(s, seat) + rel) % JK_TRACK;
const onTrack = (p: number) => p >= 0 && p < JK_TRACK;
const allHome = (s: JackarooState, seat: Seat) => s.marbles[seat].every((p) => p >= JK_TRACK);

/** Seat whose marbles `seat` moves this turn. */
function controlled(s: JackarooState, seat: Seat): Seat {
  if (allHome(s, seat) && teams(s)) return partner(s, seat);
  return seat;
}

function occupantAt(s: JackarooState, abs: number): MarbleRef | null {
  for (let seat = 0; seat < s.n; seat++) {
    for (let i = 0; i < 4; i++) {
      const p = s.marbles[seat][i];
      if (onTrack(p) && absOf(s, seat, p) === abs) return { s: seat, i };
    }
  }
  return null;
}

const isProtected = (s: JackarooState, m: MarbleRef) => s.marbles[m.s][m.i] === 0;
const same = (a: MarbleRef, b: MarbleRef) => a.s === b.s && a.i === b.i;

function capture(s: JackarooState, victim: MarbleRef, by: Seat, ev: GameEvent[]) {
  s.marbles[victim.s][victim.i] = -1;
  ev.push({ type: "captured", seat: by, victim: victim.s, marble: victim.i });
}

function doForward(s: JackarooState, m: MarbleRef, k: number, by: Seat, ev: GameEvent[]): string | null {
  const r = s.marbles[m.s]?.[m.i];
  if (r === undefined) return "bad_marble";
  if (r < 0) return "marble_in_base";
  if (r >= JK_TRACK) {
    const g = r - JK_TRACK;
    if (g + k > JK_HOME - 1) return "overshoot";
    for (let h = g + 1; h <= g + k; h++) if (s.marbles[m.s].includes(JK_TRACK + h)) return "home_blocked";
    s.marbles[m.s][m.i] = JK_TRACK + g + k;
    ev.push({ type: "moved", seat: by, owner: m.s, marble: m.i, from: r, to: r + k });
    return null;
  }
  for (let step = 1; step <= k; step++) {
    const nr = r + step;
    if (nr < JK_TRACK) {
      const occ = occupantAt(s, absOf(s, m.s, nr));
      if (occ && !same(occ, m) && isProtected(s, occ)) return "path_blocked";
    } else {
      const h = nr - JK_TRACK;
      if (h > JK_HOME - 1) return "overshoot";
      if (s.marbles[m.s].includes(JK_TRACK + h)) return "home_blocked";
    }
  }
  const to = r + k;
  if (to < JK_TRACK) {
    const occ = occupantAt(s, absOf(s, m.s, to));
    if (occ && !same(occ, m)) {
      if (occ.s === m.s) return "own_marble";
      capture(s, occ, by, ev);
    }
  }
  s.marbles[m.s][m.i] = to;
  ev.push({ type: "moved", seat: by, owner: m.s, marble: m.i, from: r, to });
  return null;
}

function doBack(s: JackarooState, m: MarbleRef, by: Seat, ev: GameEvent[]): string | null {
  const r = s.marbles[m.s]?.[m.i];
  if (r === undefined) return "bad_marble";
  if (!onTrack(r)) return "not_on_track";
  for (let step = 1; step <= 4; step++) {
    const nr = (r - step + JK_TRACK) % JK_TRACK;
    const occ = occupantAt(s, absOf(s, m.s, nr));
    if (occ && !same(occ, m) && isProtected(s, occ)) return "path_blocked";
  }
  const to = (r - 4 + JK_TRACK) % JK_TRACK;
  const occ = occupantAt(s, absOf(s, m.s, to));
  if (occ && !same(occ, m)) {
    if (occ.s === m.s) return "own_marble";
    capture(s, occ, by, ev);
  }
  s.marbles[m.s][m.i] = to;
  ev.push({ type: "moved", seat: by, owner: m.s, marble: m.i, from: r, to, back: true });
  return null;
}

function doExit(s: JackarooState, seat: Seat, ev: GameEvent[]): string | null {
  const cs = controlled(s, seat);
  const i = s.marbles[cs].indexOf(-1);
  if (i < 0) return "no_marble_in_base";
  const occ = occupantAt(s, startAbs(s, cs));
  if (occ) {
    if (occ.s === cs) return "start_occupied";
    capture(s, occ, seat, ev);
  }
  s.marbles[cs][i] = 0;
  ev.push({ type: "exited", seat, owner: cs, marble: i });
  return null;
}

function doSwap(s: JackarooState, seat: Seat, a: MarbleRef, b: MarbleRef, ev: GameEvent[]): string | null {
  const cs = controlled(s, seat);
  if (a?.s !== cs) return "not_your_marble";
  if (!b || b.s === a.s) return "bad_target";
  const pa = s.marbles[a.s]?.[a.i];
  const pb = s.marbles[b.s]?.[b.i];
  if (pa === undefined || pb === undefined) return "bad_marble";
  if (!onTrack(pa) || !onTrack(pb)) return "not_on_track";
  if (isProtected(s, a) || isProtected(s, b)) return "protected";
  const absA = absOf(s, a.s, pa);
  const absB = absOf(s, b.s, pb);
  s.marbles[a.s][a.i] = (absB - startAbs(s, a.s) + JK_TRACK) % JK_TRACK;
  s.marbles[b.s][b.i] = (absA - startAbs(s, b.s) + JK_TRACK) % JK_TRACK;
  ev.push({ type: "swapped", seat, a, b });
  return null;
}

function apply(s: JackarooState, seat: Seat, mv: JackarooMove, ev: GameEvent[]): string | null {
  const hi = s.hands[seat].indexOf(mv.card);
  if (hi < 0) return "card_not_in_hand";
  if (mv.type === "discard") {
    s.hands[seat].splice(hi, 1);
    s.discard.push(mv.card);
    ev.push({ type: "discarded", seat, card: mv.card });
    return null;
  }
  const rank = cardRank(mv.card);
  const cs = controlled(s, seat);
  let e: string | null;
  switch (mv.action) {
    case "exit":
      if (rank !== 1 && rank !== 13) return "card_cannot_exit";
      e = doExit(s, seat, ev);
      break;
    case "move": {
      const allowed =
        rank === 1 ? [1, 11] : rank === 13 ? [13] : rank === 12 ? [12] : [2, 3, 5, 6, 8, 9, 10].includes(rank) ? [rank] : [];
      if (!allowed.includes(mv.steps)) return "bad_steps";
      if (!mv.marble) return "bad_marble";
      if (rank !== 5 && mv.marble.s !== cs) return "not_your_marble";
      e = doForward(s, mv.marble, mv.steps, seat, ev);
      break;
    }
    case "back":
      if (rank !== 4) return "card_cannot_back";
      if (mv.marble?.s !== cs) return "not_your_marble";
      e = doBack(s, mv.marble, seat, ev);
      break;
    case "swap":
      if (rank !== 11) return "card_cannot_swap";
      e = doSwap(s, seat, mv.marble, mv.target, ev);
      break;
    case "split": {
      if (rank !== 7) return "card_cannot_split";
      const parts = mv.parts;
      if (!Array.isArray(parts) || parts.length < 1 || parts.length > 2) return "bad_split";
      if (parts.reduce((a, p) => a + (p?.steps | 0), 0) !== 7) return "split_must_total_7";
      if (parts.some((p) => !Number.isInteger(p.steps) || p.steps < 1)) return "bad_split";
      if (parts.length === 2 && same(parts[0].marble, parts[1].marble)) return "bad_split";
      e = null;
      for (const p of parts) {
        if (p.marble?.s !== controlled(s, seat)) {
          e = "not_your_marble";
          break;
        }
        e = doForward(s, p.marble, p.steps, seat, ev);
        if (e) break;
      }
      break;
    }
    default:
      return "unknown_action";
  }
  if (e) return e;
  s.hands[seat].splice(s.hands[seat].indexOf(mv.card), 1);
  s.discard.push(mv.card);
  s.lastPlay = { seat, card: mv.card, action: mv.action };
  return null;
}

function candidates(s: JackarooState, seat: Seat): JackarooMove[] {
  const out: JackarooMove[] = [];
  const cs = controlled(s, seat);
  const mine: MarbleRef[] = [0, 1, 2, 3].map((i) => ({ s: cs, i }));
  const placed = (m: MarbleRef) => s.marbles[m.s][m.i] >= 0;
  const all: MarbleRef[] = [];
  for (let p = 0; p < s.n; p++) for (let i = 0; i < 4; i++) all.push({ s: p, i });
  const seenRank = new Set<number>();
  for (const card of s.hands[seat]) {
    const rank = cardRank(card);
    if (seenRank.has(rank)) continue; // same rank → same options; first copy is enough
    seenRank.add(rank);
    if (rank === 1 || rank === 13) out.push({ type: "play", card, action: "exit" });
    const steps =
      rank === 1 ? [1, 11] : rank === 13 ? [13] : rank === 12 ? [12] : [2, 3, 6, 8, 9, 10].includes(rank) ? [rank] : [];
    for (const k of steps) for (const m of mine) if (placed(m)) out.push({ type: "play", card, action: "move", marble: m, steps: k });
    if (rank === 5) for (const m of all) if (placed(m)) out.push({ type: "play", card, action: "move", marble: m, steps: 5 });
    if (rank === 4) for (const m of mine) if (onTrack(s.marbles[m.s][m.i])) out.push({ type: "play", card, action: "back", marble: m });
    if (rank === 11)
      for (const a of mine)
        for (const b of all)
          if (b.s !== a.s && onTrack(s.marbles[a.s][a.i]) && onTrack(s.marbles[b.s][b.i]))
            out.push({ type: "play", card, action: "swap", marble: a, target: b });
    if (rank === 7) {
      const pool = teams(s) ? [...mine, ...[0, 1, 2, 3].map((i) => ({ s: partner(s, seat), i }))] : mine;
      for (const m of mine) if (placed(m)) out.push({ type: "play", card, action: "split", parts: [{ marble: m, steps: 7 }] });
      for (const a of mine)
        for (const b of pool)
          if (!same(a, b) && placed(a) && placed(b))
            for (let x = 1; x <= 6; x++)
              out.push({ type: "play", card, action: "split", parts: [{ marble: a, steps: x }, { marble: b, steps: 7 - x }] });
    }
  }
  return out;
}

function legalPlays(s: JackarooState, seat: Seat): JackarooMove[] {
  return candidates(s, seat).filter((mv) => apply(clone(s), seat, mv, []) === null);
}

/** Cards dealt to each player in this deal. */
function dealSize(s: JackarooState) {
  const p = s.options.dealPattern;
  return p[s.dealIndex % p.length];
}

function deal(s: JackarooState, ev: GameEvent[]) {
  const size = dealSize(s);
  if (s.deck.length < size * s.n) {
    s.deck = shuffle(s.rng, Array.from({ length: 52 }, (_, i) => i));
    s.discard = [];
    ev.push({ type: "reshuffled" });
  }
  for (let p = 0; p < s.n; p++) s.hands[p] = s.deck.splice(0, size);
  s.dealIndex++;
  s.dealer = (s.dealer + 1) % s.n;
  s.turn = (s.dealer + 1) % s.n;
  ev.push({ type: "dealt", size, dealer: s.dealer });
}

function checkWin(s: JackarooState, ev: GameEvent[]) {
  for (let seat = 0; seat < s.n; seat++) {
    const done = teams(s) ? allHome(s, seat) && allHome(s, partner(s, seat)) : allHome(s, seat);
    if (done) {
      s.over = true;
      s.winners = teams(s) ? [seat % 2, (seat % 2) + 2] : [seat];
      ev.push({ type: "game_won", winners: s.winners });
      return;
    }
  }
}

export const jackaroo: GameEngine<JackarooState, JackarooMove, JackarooView, JackarooOptions> = {
  id: "jackaroo",
  minPlayers: 2,
  maxPlayers: 4,
  playerCounts: [2, 4],
  defaultOptions: { dealPattern: [5, 4, 4] },

  init(n, seed, options) {
    if (n !== 2 && n !== 4) throw new Error("jackaroo supports 2 or 4 players");
    const s: JackarooState = {
      n,
      boardPos: n === 4 ? [0, 1, 2, 3] : [0, 2],
      marbles: Array.from({ length: n }, () => [-1, -1, -1, -1]),
      hands: Array.from({ length: n }, () => []),
      deck: [],
      discard: [],
      turn: 0,
      dealer: n - 1,
      dealIndex: 0,
      over: false,
      winners: [],
      options: { ...this.defaultOptions, ...options },
      rng: { s: seed | 0 },
      lastPlay: null,
    };
    s.deck = shuffle(s.rng, Array.from({ length: 52 }, (_, i) => i));
    s.dealer = n - 1;
    deal(s, []);
    return s;
  },

  currentSeat: (s) => (s.over ? null : s.turn),

  applyMove(state, seat, move) {
    if (state.over) return err("game_over");
    if (seat !== state.turn) return err("not_your_turn");
    if (!move || typeof move.card !== "number") return err("bad_move");
    const s = clone(state);
    const ev: GameEvent[] = [];
    if (move.type === "discard") {
      if (legalPlays(s, seat).length) return err("must_play_a_card");
    } else if (move.type !== "play") return err("unknown_move");
    const e = apply(s, seat, move, ev);
    if (e) return err(e);
    checkWin(s, ev);
    if (s.over) return { ok: true, state: s, events: ev };
    // next player with cards; deal when everyone is empty
    let next = (seat + 1) % s.n;
    let guard = 0;
    while (s.hands[next].length === 0 && guard++ < s.n) next = (next + 1) % s.n;
    if (s.hands.every((h) => h.length === 0)) deal(s, ev);
    else s.turn = next;
    return { ok: true, state: s, events: ev };
  },

  view(s, seat) {
    const mine = seat >= 0 && seat < s.n;
    const legal = mine && seat === s.turn && !s.over ? legalPlays(s, seat) : [];
    return {
      n: s.n,
      boardPos: s.boardPos.slice(),
      marbles: clone(s.marbles),
      hand: mine ? s.hands[seat].slice() : [],
      handCounts: s.hands.map((h) => h.length),
      deckCount: s.deck.length,
      topDiscard: s.discard.length ? s.discard[s.discard.length - 1] : null,
      turn: s.turn,
      over: s.over,
      winners: s.winners.slice(),
      teams: teams(s),
      lastPlay: s.lastPlay,
      legal,
      mustDiscard: mine && seat === s.turn && !s.over && legal.length === 0,
    };
  },

  result(s) {
    if (!s.over) return null;
    const seats = Array.from({ length: s.n }, (_, i) => i);
    const losers = seats.filter((x) => !s.winners.includes(x));
    const home = (x: number) => s.marbles[x].filter((p) => p >= JK_TRACK).length;
    return {
      ranking: [s.winners, losers],
      scores: seats.map(home),
      reason: "all_marbles_home",
    };
  },

  autoMove(s, seat) {
    if (s.over || s.turn !== seat) return null;
    const legal = legalPlays(s, seat);
    if (legal.length) return legal[0];
    return { type: "discard", card: s.hands[seat][0] };
  },
};
